import { z } from 'zod';
import { MARKUP_MOVE_CONSTRAINTS, MARKUP_TYPES, MEDIA_MAX_ITEMS, MEDIA_MIMES, MEDIA_SOURCE_KINDS, ROOM_SOURCES, REVIEW_NOTE_MAX_LENGTH, REVIEW_SELECTION, REVIEW_TRACE, REVIEW_STATUSES, type MarkupType } from '@bim-pdf/core';
import { UnitSystem, TextSize } from './enums';
import type { CountSymbol } from './primitives';

/**
 * Runtime schemas for the app-PRIVATE persisted shapes -- every domain type
 * READ back from Firestore, plus the other app-owned persistence surfaces
 * (the `/PMT` annotation metadata, localStorage shapes) -- and the TypeScript
 * types derived from them via `z.infer` (the single source of truth -- the
 * `interface` definitions these replaced could drift from what the schema
 * actually validated). FirestoreService parses each document against the
 * matching schema at the read boundary and DROPS anything that does not match,
 * so malformed / legacy-shaped documents never reach the app as a mistyped
 * object hidden behind an `as X` cast.
 *
 * Ownership boundary (docs/decisions/2026-08-13-validation-ownership-boundary.md):
 * app-private persisted shapes are validated with zod HERE (or colocated, e.g.
 * PersistedMarkupLayerSchema in core/markups/layers.ts); the PUBLISHED bim-pdf
 * interchange format is validated by packages/core's AJV/JSON-Schema, never by
 * zod in this app.
 *
 * Nested value types (Point, MarkupStyle) intentionally stay as interfaces in
 * index.ts; the schemas below mirror them structurally, which is all TypeScript
 * needs for the inferred document types to line up with the rest of the app.
 */

// ==================== Nested value schemas ====================

export const PointSchema = z.object({ x: z.number(), y: z.number() });

const TextFormattingSchema = z.object({
  bold: z.boolean().optional(),
  underline: z.boolean().optional(),
  strikethrough: z.boolean().optional(),
});

const MarkupStyleSchema = z.object({
  color: z.string(),
  lineWidth: z.number(),
  fill: z.string().optional(),
  opacity: z.number().optional(),
  lineStyle: z.enum(['solid', 'dashed', 'dotted']).optional(),
  textSize: z.enum([TextSize.SMALL, TextSize.MEDIUM, TextSize.LARGE] as const).optional(),
  textFormatting: TextFormattingSchema.optional(),
  fontFamily: z.string().optional(),
  fontSize: z.number().optional(),
  countSymbol: z.enum(['circle', 'square', 'triangle', 'x', 'cross'] as const satisfies readonly CountSymbol[]).optional(),
});

// ==================== Review-loop value schemas ====================
// Mirror the bim-pdf 1.11 Annotation review fields (ReviewStatus,
// StatusHistoryEntry, MarkupDelta). The status enum is core's runtime list, so
// the two regimes cannot disagree on the set of statuses.

export const ReviewStatusSchema = z.enum(REVIEW_STATUSES);

export const ReviewAuthorSchema = z.object({
  id: z.string(),
  display: z.string(),
  type: z.enum(['human', 'agent']),
});
export type ReviewAuthor = z.infer<typeof ReviewAuthorSchema>;

export const StatusHistoryEntrySchema = z.object({
  status: ReviewStatusSchema,
  by: ReviewAuthorSchema,
  /** ISO 8601 datetime string (same encoding as annotations.json). */
  at: z.string(),
  /** History-only marker (review contract v2): `withdrawn` on the drafter's
   *  proposed -> open entry when it retracts its own proposal; `apply-refused`
   *  (v3) on the drafter's needs-human entry when markups.apply refused an
   *  accepted set (accepted > applying > needs-human); `anchor-declared` (v4)
   *  on the drafter's grounded entry when it DECLARED the anchor (markups.ground
   *  --anchor; reason = the spec). Not a status. */
  event: z.enum(['withdrawn', 'apply-refused', 'anchor-declared', 'executed-resolved']).optional(),
  /** Free text recorded with the entry (the drafter's withdraw / refusal reason). */
  reason: z.string().optional(),
  /** LEGACY (contract v5-v6), read-only: the reviewer's short "meant something
   *  else" note on the `rejected` entry of a human Reject. Contract v7 writes it
   *  as a reviewer_events `note` event instead (the rules deny a new one here). */
  note: z.string().max(REVIEW_NOTE_MAX_LENGTH).optional(),
});
export type StatusHistoryEntry = z.infer<typeof StatusHistoryEntrySchema>;

/** Contract v5: one candidate reading of a proposal (delta.alternatives[i]). */
export const DeltaAlternativeSchema = z.object({
  summary: z.string(),
  delta: z.object({
    driver: z.string(),
    verb: z.string(),
    args: z.record(z.string(), z.unknown()),
  }),
  confidence: z.number().min(0).max(1),
});
export type DeltaAlternative = z.infer<typeof DeltaAlternativeSchema>;

export const MarkupDeltaSchema = z.object({
  driver: z.string(),
  verb: z.string(),
  args: z.record(z.string(), z.unknown()),
  preimage: z.record(z.string(), z.unknown()).optional(),
  // Rev when the proposal became visible (pdf-cli >= 0.6.0). Informational since
  // pdf-cli 0.6.3: apply checks invariants (history, delta.hash), not rev arithmetic.
  base_rev: z.number().int().min(1),
  base_revs: z.record(z.string(), z.number().int().min(1)).optional(),
  base_package_hash: z.string().optional(),
  summary: z.string(),
  // pdf-cli >= 0.6.3: sha256 of the delta without `hash`; apply refuses when it no longer matches.
  hash: z.string().optional(),
  // Contract v5 READING ALTERNATIVES (drafter-only): candidate readings, best
  // first; alternatives[0] is the top-level driver/verb/args. The hash covers them.
  alternatives: z.array(DeltaAlternativeSchema).max(REVIEW_SELECTION.maxAlternatives).optional(),
  // Contract v5: the reviewer's pick, written ONLY inside the human Accept as the
  // field path `delta.selected`. apply runs alternatives[selected] (absent = 0).
  selected: z.number().int().min(0).optional(),
  // Contract v6 INTENT TRACE (drafter-only, covered by the hash): the reply id of
  // the proposal this one revises (a late stroke joined the intent), which
  // revision this is (absent = 1), and the intent's trace id (same on every
  // revision; the review telemetry carries it).
  revision_of: z.string().min(1).optional(),
  revision: z.number().int().min(1).optional(),
  previous_summary: z.string().max(600).optional(),
  trace_id: z.string().regex(new RegExp(REVIEW_TRACE.pattern)).optional(),
});
export type MarkupDelta = z.infer<typeof MarkupDeltaSchema>;

// ==================== Contract v7: request + review ====================
// The v2-v6 `delta` (above, now a read-only legacy shape) is split into the
// EXECUTABLE `request` and the RECORD `review` (packages/core reviewRecord.ts;
// review-firestore.schema.json MarkupRequest / MarkupReview). Readers go through
// core's reviewRecordOf() / deltaView(), which accept either shape.

export const TraceIdSchema = z.string().regex(new RegExp(REVIEW_TRACE.pattern));

/** Contract v8: projects/{p}/operations/{op_id} id (op- + 20 [a-z0-9]; review-firestore.schema.json OpId). */
export const OpIdSchema = z.string().regex(/^op-[a-z0-9]{20}$/);

// ---- Drafter LIVE PROGRESS (review-firestore.schema.json AgentStatus) ----
// Written ONLY by the drafter (pdf-cli, Admin SDK) on every markup of an
// intent, overwritten per stage; cleared or left at done / failed. The rules
// deny it to every client write. Display only (the review rail's status chip):
// nothing in the review loop reads it as state.
export const AGENT_STAGES = ['reading', 'read', 'drafting', 'verifying', 'proposing', 'applying', 'applied', 'exporting', 'ingesting', 'done', 'failed'] as const;

export const AgentStatusSchema = z.object({
  stage: z.enum(AGENT_STAGES),
  text: z.string().max(200),
  /** ISO 8601 datetime string of this stage's write. */
  at: z.string().min(1),
  trace_id: TraceIdSchema.optional(),
});
export type AgentStatus = z.infer<typeof AgentStatusSchema>;

// ==================== The revision model (organizing model v3) ====================
// Stored shapes: review-firestore.schema.json Revision / SetEntry / DrawnOn /
// RevisionPage. Written ONLY by the review service (review.ingest); the SPA
// reads them. Every page number here is 1-based.

/** The document revision a markup / stroke / intent was drawn on. */
export const DrawnOnSchema = z.object({ documentId: z.string(), revision: z.string() });

/**
 * The `move` object of a `type: "move"` markup (review-firestore.schema.json
 * #/definitions/MarkupMove; core MarkupMove): which element, in which viewport /
 * view, by how much in RAW page points Y-up, and how the drag was snapped and
 * constrained. Written once by the Move tool; the drafter reads it.
 */
export const MarkupMoveSchema = z.object({
  element_id: z.string().min(1).max(128),
  page_vp: z.number().int().min(0),
  view_id: z.string().min(1).max(256),
  delta_pt: z.tuple([z.number(), z.number()]),
  snap: z.object({ kind: z.string().min(1).max(32), to: z.string().min(1).max(128).optional() }).optional(),
  constraint: z.enum(MARKUP_MOVE_CONSTRAINTS).optional(),
});

/** An http(s) URL (never javascript: / data:); checked again before any navigation. */
export function isHttpUrl(s: string): boolean {
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** A media item id: client-generated, stable, the key of `media.<id>` dotted-path writes. */
export const MEDIA_ITEM_ID = /^[A-Za-z0-9_-]{8,24}$/;

/**
 * One photo on a markup (review-firestore.schema.json #/definitions/MediaItem; core MediaItem; spec
 * pdfmarkuptool-image-markup 3.2): a reference to a content-addressed asset and its metadata, never bytes.
 */
export const MediaItemSchema = z.object({
  asset: z.string().regex(/^[0-9a-f]{64}$/),
  mime: z.enum(MEDIA_MIMES),
  w: z.number().int().min(1).max(4096),
  h: z.number().int().min(1).max(4096),
  order: z.number(),
  caption: z.string().max(500).optional(),
  capturedAt: z.number().optional(),
  addedBy: z.string(),
  addedAt: z.number(),
  source: z.object({
    kind: z.enum(MEDIA_SOURCE_KINDS),
    url: z.string().max(2048).refine(isHttpUrl).optional(),
  }),
});
export type MediaItem = z.infer<typeof MediaItemSchema>;

/** A markup's photos: a map keyed by item id (dotted-path writes merge; never a list), at most MEDIA_MAX_ITEMS. */
export const MediaSchema = z
  .record(z.string().regex(MEDIA_ITEM_ID), MediaItemSchema)
  .refine((m) => Object.keys(m).length <= MEDIA_MAX_ITEMS, { message: `at most ${MEDIA_MAX_ITEMS} media items` });
export type Media = z.infer<typeof MediaSchema>;

/** The room a photo pin was placed in, as a snapshot taken at link time (#/definitions/MarkupRoom). */
export const MarkupRoomSchema = z.object({
  elementId: z.string().max(128).optional(),
  number: z.string().max(40).optional(),
  name: z.string().max(120).optional(),
  from: z.enum(ROOM_SOURCES),
});
export type MarkupRoomValue = z.infer<typeof MarkupRoomSchema>;
export type DrawnOn = z.infer<typeof DrawnOnSchema>;

/** One page of a revision (page = 1-based page of that revision's PDF). */
export const RevisionPageSchema = z.object({
  page: z.number().int().min(1),
  page_id: z.string(),
  // document.upsert (document control): the content-addressed page + its slice
  page_hash: z.string().optional(),
  sidecar_hash: z.string().optional(),
  label: z.string().optional(),
  sheet_number: z.string().optional(),
  geometry: z
    .object({ media_box: z.array(z.number()), crop_box: z.array(z.number()).optional(), rotate: z.number().optional() })
    .optional(),
});
export type RevisionPage = z.infer<typeof RevisionPageSchema>;

/** One entry of the project Set index; array order = 1-based display order. */
export const SetEntrySchema = z.object({
  page_id: z.string(),
  documentId: z.string(),
  revision: z.string(),
  page: z.number().int().min(1),
  sheet_number: z.string().optional(),
});
export type SetEntry = z.infer<typeof SetEntrySchema>;

/** Where a document comes from: a bim-cli driver (+ source document / file), or 'none' (a sketch). */
export const DocumentSourceSchema = z.object({ driver: z.string(), document_id: z.string().optional(), file: z.string().optional() });

/**
 * projects/{p}/documents/{d}/revisions/{r}: immutable. A review.ingest revision
 * is one whole package (packageHash + storagePath); a document.upsert revision
 * lists content-addressed pages + docdata + diff and carries packageHash +
 * storagePath only with its assembled PDF (FirestoreService maps them from
 * `assembled` when absent).
 */
export const RevisionSchema = z.object({
  id: z.string(),
  n: z.number().int().min(1),
  packageHash: z.string().optional(),
  storagePath: z.string().optional(),
  pages: z.array(RevisionPageSchema),
  parent: z.string().nullable(),
  source: DocumentSourceSchema.optional(),
  docdata: z.record(z.string(), z.string()).optional(),
  diff: z
    .object({ added: z.array(z.string()), changed: z.array(z.string()), unchanged: z.array(z.string()), removed: z.array(z.string()) })
    .optional(),
  assembled: z.object({ path: z.string(), packageHash: z.string() }).optional(),
  mode: z.enum(['replace', 'patch']).optional(),
  op_hash: z.string().optional(),
  op_id: z.string(),
  createdAt: z.date(),
  createdBy: z.object({ id: z.string(), type: z.enum(['human', 'agent']), actor: z.string() }),
});
export type Revision = z.infer<typeof RevisionSchema>;

export const MarkupRequestSchema = z.object({
  driver: z.string(),
  verb: z.string(),
  args: z.record(z.string(), z.unknown()),
  preimage: z.record(z.string(), z.unknown()).optional(),
  base_package_hash: z.string().optional(),
  // The revision model: {documentId: revision} the request was computed against;
  // apply is stale only when one of THESE documents has a newer revision.
  base_revisions: z.record(z.string(), z.string()).optional(),
  script: z.object({ file: z.string(), sha256: z.string() }).optional(),
});
export type MarkupRequest = z.infer<typeof MarkupRequestSchema>;

/** One candidate reading (review.alternatives[i]). */
export const ReviewAlternativeSchema = z.object({
  summary: z.string(),
  request: z.object({ driver: z.string(), verb: z.string(), args: z.record(z.string(), z.unknown()) }),
  confidence: z.number().min(0).max(1),
});
export type ReviewAlternative = z.infer<typeof ReviewAlternativeSchema>;

export const ReviewOutcomeSchema = z.object({
  kind: z.enum(['applied', 'executed', 'filed', 'failed']),
  result: z.unknown().optional(),
  at: z.string(),
  // Contract v8: the operation record (projects/{p}/operations/{op_id}) this outcome's apply created.
  op_id: OpIdSchema.optional(),
});

export const MarkupReviewSchema = z.object({
  hash: z.string(),
  summary: z.string(),
  base_rev: z.number().int().min(1).optional(),
  base_revs: z.record(z.string(), z.number().int().min(1)).optional(),
  alternatives: z.array(ReviewAlternativeSchema).max(REVIEW_SELECTION.maxAlternatives).optional(),
  // The ONE client write into a drafter field: the Accept's pick (field path review.selected).
  selected: z.number().int().min(0).optional(),
  revision: z.number().int().min(1).optional(),
  revision_of: z.string().min(1).optional(),
  previous_summary: z.string().max(600).optional(),
  trace_id: TraceIdSchema,
  outcome: ReviewOutcomeSchema.optional(),
});
export type MarkupReview = z.infer<typeof MarkupReviewSchema>;

// ==================== Markup schemas ====================

/**
 * Core markup types this app never persists: producer-extracted annotation
 * types (Revit tags/details/other) that arrive in a bim-pdf package but are
 * never authored here. Everything else in core's MARKUP_TYPES is persistable.
 */
const PRODUCER_ONLY_MARKUP_TYPES = ['tag', 'detail', 'other'] as const satisfies readonly MarkupType[];
type ProducerOnlyMarkupType = (typeof PRODUCER_ONLY_MARKUP_TYPES)[number];

const isPersistableMarkupType = (t: MarkupType): t is Exclude<MarkupType, ProducerOnlyMarkupType> =>
  !(PRODUCER_ONLY_MARKUP_TYPES as readonly MarkupType[]).includes(t);

/**
 * The discriminant set of MarkupSchema: DERIVED from core's MARKUP_TYPES (the
 * single source; architecture review 2026-09-23, 1.4) minus the producer-only
 * types. Adding a type to core adds it here, and the variant table below then
 * fails typecheck until the type has a variant. Exported for the agent input
 * schemas (useViewerTestApi) and drift tests.
 */
export const MARKUP_TYPE_VALUES = MARKUP_TYPES.filter(isPersistableMarkupType) as unknown as readonly [
  Exclude<MarkupType, ProducerOnlyMarkupType>,
  ...Exclude<MarkupType, ProducerOnlyMarkupType>[],
];

/**
 * The markup types this app can draw/edit on the canvas — the 14 values above,
 * excluding the producer-extracted types (dimension/tag/detail/other) that
 * arrive via a bim-pdf producer but are never authored here. Use this (not the
 * wider core `MarkupType`) for tool pickers.
 */
export type DrawableMarkupType = (typeof MARKUP_TYPE_VALUES)[number];

const baseMarkupShape = {
  id: z.string(),
  projectId: z.string(),
  documentId: z.string(),
  pageNumber: z.number(),
  type: z.enum(MARKUP_TYPE_VALUES),
  subject: z.string(),
  points: z.array(PointSchema),
  style: MarkupStyleSchema,
  createdByUserId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  groupId: z.string().optional(),
  layer_id: z.string().optional(),
  visible: z.boolean().optional(),
  printable: z.boolean().optional(),
  locked: z.boolean().optional(),
  zIndex: z.number().optional(),
  // Rotation in degrees (clockwise, around the bounding-box center) for the
  // box-based rotatable markups (rectangle/ellipse/snapshot/stamp/text). The
  // shape in `points` stays axis-aligned; rendering, hit-testing and bake apply
  // this angle as a transform. Absent/0 = unrotated. See core/markup-geometry/rotation.
  rotation: z.number().optional(),
  // Link to bim-pdf elements (elements.json `id`s). A single-target markup (a
  // callout whose tip lands on one element) is a one-entry array; a region markup
  // (a revision cloud enclosing several elements) carries many. Resolved by
  // containment/hit-test at draw time and stored (stable at rest). Declared on the
  // shared base so it survives zod's unknown-key stripping for every markup type.
  // Read via `elementIdsOf(markup)` for a safe empty-array default.
  linkedElementIds: z.array(z.string()).optional(),
  // For a callout attached to another markup (a revision cloud): the id of the
  // cloud this callout labels. When set, the callout renders as a connector to the
  // cloud (pivoting on the cloud centroid) instead of an arrow to a point, and its
  // text becomes the label for the cloud's linked element set.
  attachedMarkupId: z.string().optional(),
  // Photos on the markup (image markup spec 3.2): any type may carry up to 24 (a
  // callout with its site photo); `image` holds exactly one and `photo-pin` 1..24
  // (their variants below). References to content-addressed assets, never bytes.
  media: MediaSchema.optional(),
  // ---- The revision model (organizing model v3; review.ingest re-anchors by these) ----
  // The stable identity of the page this markup is on (bim-pdf 1.12 pages.json
  // page_id). pageNumber stays the display index; review.ingest rewrites it when
  // the page moves and parks the markup when the page is removed or its geometry
  // changes. Absent on markups from before revisions.
  page_id: z.string().optional(),
  // The document revision the markup was drawn on.
  drawn_on: DrawnOnSchema.optional(),
  // ---- Markup review loop (bim-pdf 1.11; contract: packages/core Annotation) ----
  // Same field names as the published annotations.json Annotation so the
  // emitter, the Firestore document and the rules all speak one vocabulary.
  // Defaults apply at the read boundary, so pre-1.11 documents parse as a
  // fresh `open` markup at rev 1.
  // Monotonic per-markup revision: the store adapters bump it by exactly 1 on
  // every write (see core/markups/revision.ts, ADR 2026-09-22-markup-rev-and-review-role).
  rev: z.number().int().min(1).default(1),
  status: ReviewStatusSchema.default('open'),
  status_history: z.array(StatusHistoryEntrySchema).default([]),
  // A drafter's proposal is a reply markup pointing at the human's markup.
  in_reply_to: z.string().optional(),
  // LEGACY (contract v2-v6) proposal, read-only: a pre-v7 document keeps it
  // until the drafter's next write upgrades it. Read it through core deltaView().
  delta: MarkupDeltaSchema.optional(),
  // ---- Contract v7 (drafter-only, pdf-cli >= 0.7.0) ----
  // The document's review contract version: absent = client-created or pre-v7. The floor is
  // FIXED at 7 (when request/review/trace_id were introduced), not REVIEW_CONTRACT_VERSION --
  // that constant tracks the CURRENT contract (8+), and a document a prior release wrote at
  // exactly 7 stays a valid historical document (additive bumps, e.g. v8's operations/restore,
  // never raise this floor).
  contractVersion: z.number().int().min(7).optional(),
  // ONE IDENTITY: the intent this markup belongs to (every member, reply, group).
  trace_id: TraceIdSchema.optional(),
  // The proposal: the executable and the record (primary + reply).
  request: MarkupRequestSchema.optional(),
  review: MarkupReviewSchema.optional(),
  // Why the drafter moved this markup to `needs-human` (ungrounded, apply
  // failed, proposal expired). Written only by the drafter (pdf-cli
  // markups.* verbs, Admin SDK); read-only in the SPA, shown in the review UI.
  needs_human_reason: z.string().optional(),
  // ---- Dispatch lock (contract dispatchLock, 2026-10-06, spec--review-revamp R2) ----
  // The dispatch event id that grouped and LOCKED this markup (written by the
  // reviewer's Dispatch, cleared by Reopen / Release): no geometry / text / style /
  // grouping edit and no delete while set.
  dispatch_id: z.string().optional(),
  // A resolved / restored markup the reviewer Released: hidden with its request.
  released: z.boolean().optional(),
  // The request store (reviewCore 2, wiki spec--review-revamp R5): the request this ink
  // belongs to -- written only by the review service (null once freed).
  request_id: z.string().nullable().optional(),
  // ---- Drafter-written, read-only in the SPA (pdf-cli markups.* verbs) ----
  // Carried so a read -> restore round trip keeps them instead of dropping them
  // (their stored shapes: packages/core/src/schema/review-firestore.schema.json).
  // markups.propose, on the ORIGINAL: the proposal reply callout's id.
  proposal_markup_id: z.string().optional(),
  // markups.propose, on the proposal REPLY (contract v2 proposal sets): the
  // markup ids the proposal covers, primary first. The reply and the set share
  // one group (groupId), so the proposal is one reviewable unit.
  addresses: z.array(z.string()).optional(),
  // markups.propose: when a still-proposed markup is swept to needs-human.
  proposal_expires_at: z.date().optional(),
  // markups.ingest: the package hash the markup was resolved against.
  resolved_package_hash: z.string().optional(),
  // markups.apply: the idempotency journal of one apply attempt. Opaque here
  // (Timestamps inside are carried as-is); pdf-cli owns its fields.
  apply: z.record(z.string(), z.unknown()).optional(),
  // markups.ground --anchor sheet:<n>[@x,y,w,h] (review contract v4): the
  // placement a CREATION intent is anchored to when no BIM element lies under
  // it (linkedElementIds stays empty). Sheet feet from the sheet's lower-left,
  // x/y = the region's top-left corner. Drafter-only (Admin SDK).
  sheet_anchor: z
    .object({
      sheet: z.string().min(1),
      x_ft: z.number().optional(),
      y_ft: z.number().optional(),
      w_ft: z.number().min(0).optional(),
      h_ft: z.number().min(0).optional(),
    })
    .optional(),
  // The drafter's live progress on this markup (AgentStatusSchema). A value that
  // does not parse (a stage from a newer drafter) reads as absent instead of
  // dropping the whole markup at the read boundary: it is display only.
  agent_status: AgentStatusSchema.optional().catch(undefined),
};

// Each variant spreads the shared fields and pins `type` to a literal (the union
// discriminant). The literal must be inline -- a generic helper widens it back to
// MarkupType and breaks both the discriminated union and inference. Unknown keys
// are stripped by zod's default behaviour, so loosely-carried extras (e.g. a
// persisted length/area on a line) drop off.
//
// The table is keyed by EVERY persistable type (a mapped type over
// DrawableMarkupType), so a type added to core's MARKUP_TYPES without a variant
// here fails typecheck; the `type` literal of each variant must match its key.
const MARKUP_VARIANTS = {
  line: z.object({ ...baseMarkupShape, type: z.literal('line') }),
  polyline: z.object({ ...baseMarkupShape, type: z.literal('polyline') }),
  polygon: z.object({ ...baseMarkupShape, type: z.literal('polygon') }),
  rectangle: z.object({ ...baseMarkupShape, type: z.literal('rectangle') }),
  ellipse: z.object({ ...baseMarkupShape, type: z.literal('ellipse') }),
  // Pen pressure per point (0..1), parallel to `points`: freehand ONLY -- the
  // one markup whose width varies (core/ink/pressureStroke.ts). Optional and
  // additive: absent on mouse strokes and on every stroke saved before it.
  freehand: z.object({ ...baseMarkupShape, type: z.literal('freehand'), pressures: z.array(z.number()).optional() }),
  arrow: z.object({ ...baseMarkupShape, type: z.literal('arrow') }),
  highlight: z.object({ ...baseMarkupShape, type: z.literal('highlight') }),
  cloud: z.object({ ...baseMarkupShape, type: z.literal('cloud') }),
  count: z.object({ ...baseMarkupShape, type: z.literal('count') }),
  callout: z.object({ ...baseMarkupShape, type: z.literal('callout'), text: z.string() }),
  text: z.object({ ...baseMarkupShape, type: z.literal('text'), text: z.string() }),
  stamp: z.object({
    ...baseMarkupShape,
    type: z.literal('stamp'),
    stampType: z.enum(['APPROVED', 'REVIEWED', 'FOR REVIEW', 'VOID']),
    stampDate: z.string(),
    stampInitials: z.string(),
  }),
  group: z.object({ ...baseMarkupShape, type: z.literal('group'), childIds: z.array(z.string()) }),
  // A dimension string: an ordered chain of vertices (2+). Each consecutive pair
  // is one dimensioned segment, auto-labeled with its real-world length at the
  // segment's local viewport scale. Same `points` shape as polyline.
  dimension: z.object({ ...baseMarkupShape, type: z.literal('dimension') }),
  // A snapshot: a captured region of PDF vector content, dragged as a bounding
  // box (points = 4 corners, same shape as rectangle). The heavy captured payload
  // (vector primitives + optional raster) lives in an in-memory store keyed by id
  // (local-first), NOT in the persisted document -- so the schema carries only the
  // placement geometry, and `captureHash` lets a rehydrated markup detect a missing
  // payload. Persistence is via baking the payload into the exported PDF.
  snapshot: z.object({ ...baseMarkupShape, type: z.literal('snapshot'), captureHash: z.string().optional() }),
  // An element move (BIM mode's Move tool, 2026-10-08): points = [centre before,
  // centre after] (canvas space, like an arrow), linkedElementIds = [the element],
  // and the move itself -- read by the drafter, which moves the element in the
  // view's own plane without a drafting step.
  move: z.object({ ...baseMarkupShape, type: z.literal('move'), move: MarkupMoveSchema }),
  // An inline image (moodboard; image markup spec 3.2): points = its 4-corner box,
  // aspect-locked to the photo, rotated by the base `rotation`; EXACTLY one media
  // item (a second would reach neither the sheet nor the report). Never converts to
  // a photo-pin (type is immutable: the rules and the local guard enforce it).
  image: z.object({
    ...baseMarkupShape,
    type: z.literal('image'),
    media: MediaSchema.refine((m) => Object.keys(m).length === 1, { message: 'an image holds exactly one media item' }),
  }),
  // A site-photo pin: points = [the anchor]; 1..24 media items (the cover is the
  // lowest order); seq / heading / room exist on this type only.
  'photo-pin': z.object({
    ...baseMarkupShape,
    type: z.literal('photo-pin'),
    media: MediaSchema.refine((m) => Object.keys(m).length >= 1, { message: 'a photo pin holds at least one media item' }),
    // The in-app label hint ("P12"); final numbers are assigned at export.
    seq: z.number().int().positive().optional(),
    // View direction on the page in degrees, [0, 360): the pin's wedge.
    heading: z.number().min(0).lt(360).optional(),
    // The room snapshot taken at link time.
    room: MarkupRoomSchema.optional(),
  }),
} satisfies { [K in DrawableMarkupType]: z.ZodObject<{ type: z.ZodLiteral<K> } & Omit<typeof baseMarkupShape, 'type' | 'media'>> };

type MarkupVariant = (typeof MARKUP_VARIANTS)[DrawableMarkupType];

// Exhaustiveness the other way: every variant key is a persistable type (an
// extra key -- a type core no longer lists -- fails here).
const variantKeysArePersistable: readonly DrawableMarkupType[] = Object.keys(MARKUP_VARIANTS) as (keyof typeof MARKUP_VARIANTS)[];
void variantKeysArePersistable;

/** The persisted markup union, one variant per MARKUP_TYPE_VALUES entry, in that order. */
export const MarkupSchema = z.discriminatedUnion(
  'type',
  MARKUP_TYPE_VALUES.map((type) => MARKUP_VARIANTS[type]) as unknown as [MarkupVariant, ...MarkupVariant[]],
);


export type Markup = z.infer<typeof MarkupSchema>;

// Per-variant types recovered from the union discriminant (barely used
// externally, but a few call sites and the *WithStats derived types want them).
export type LineMarkup = Extract<Markup, { type: 'line' }>;
export type PolylineMarkup = Extract<Markup, { type: 'polyline' }>;
export type PolygonMarkup = Extract<Markup, { type: 'polygon' }>;
export type RectangleMarkup = Extract<Markup, { type: 'rectangle' }>;
export type EllipseMarkup = Extract<Markup, { type: 'ellipse' }>;
export type FreehandMarkup = Extract<Markup, { type: 'freehand' }>;
export type ArrowMarkup = Extract<Markup, { type: 'arrow' }>;
export type HighlightMarkup = Extract<Markup, { type: 'highlight' }>;
export type CloudMarkup = Extract<Markup, { type: 'cloud' }>;
export type CalloutMarkup = Extract<Markup, { type: 'callout' }>;
export type CountMarkup = Extract<Markup, { type: 'count' }>;
export type StampMarkup = Extract<Markup, { type: 'stamp' }>;
export type TextMarkup = Extract<Markup, { type: 'text' }>;
export type GroupMarkup = Extract<Markup, { type: 'group' }>;

// The fields common to every markup, with the discriminant left as the full
// markup-type union (recovered from a variant so there's no unused schema const).
export type BaseMarkup = Omit<LineMarkup, 'type'> & { type: Markup['type'] };

// ==================== Document schemas ====================

export const ProjectSchema = z.object({
  id: z.string(),
  ownerId: z.string(),
  ownerEmail: z.string(),
  name: z.string(),
  status: z.enum(['active', 'archived']),
  unitSystem: z.enum([UnitSystem.METRIC, UnitSystem.IMPERIAL] as const),
  createdAt: z.date(),
  updatedAt: z.date(),
  cursorThrottleMs: z.number().optional(),
  // Review sessions (drafter-written via markups.session-new / ingest).
  sessionKind: z.literal('review').optional(),
  packageHash: z.string().optional(),
  /** @deprecated 1-based (sheet number -> page); kept for old readers. Use `set` / `sheetPages`. */
  sheetMap: z.record(z.string(), z.number()).optional(),
  // The revision model (service-written by review.ingest): the Set index in
  // 1-based display order, and sheet number -> 1-based Set position.
  set: z.array(SetEntrySchema).optional(),
  sheetPages: z.record(z.string(), z.number()).optional(),
  documentId: z.string().optional(),
  // The request store's gate (wiki spec--review-revamp R5): drafter-written, 2 = requests as records.
  reviewCore: z.number().optional(),
  // Service-only (U2): the highest request number assigned so far (review-request-contract.json number.counter).
  requestNumber: z.number().optional(),
});
export type Project = z.infer<typeof ProjectSchema>;

export const DocumentSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string(),
  storagePath: z.string(),
  /** @deprecated legacy field stripped by the Firestore mapper; read PDFs from authenticated Storage paths. */
  downloadURL: z.string().optional(),
  pageCount: z.number(),
  fileSizeBytes: z.number(),
  uploadUserId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date().optional(),
  // Review sessions (drafter-written): `markups.ingest` replaces the package in
  // place and bumps `version`, so the open viewer can swap the sheet live.
  packageHash: z.string().optional(),
  /** @deprecated never consumed; pdf-cli stopped writing it (2026-09-23). Use `version`. */
  packageVersion: z.number().optional(),
  version: z.number().optional(),
  // The revision model: the CURRENT immutable revision id (review.ingest, CAS).
  revision: z.string().optional(),
  // Document control (document.upsert): where the document comes from.
  source: DocumentSourceSchema.optional(),
});
export type Document = z.infer<typeof DocumentSchema>;

export const PermissionSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  userId: z.string(),
  email: z.string(),
  role: z.enum(['admin', 'editor', 'viewer']),
  scope: z.enum(['project', 'document']),
  status: z.enum(['pending', 'active']),
  documentId: z.string().optional(),
  invitedBy: z.string(),
  invitedAt: z.date(),
  // Set by the activatePendingInvites function.
  activatedAt: z.date().optional(),
});
export type Permission = z.infer<typeof PermissionSchema>;

export const ShareLinkSchema = z.object({
  token: z.string(),
  projectId: z.string(),
  documentId: z.string(),
  active: z.boolean(),
  createdBy: z.string(),
  createdAt: z.date(),
  // What the guest viewer shows of the project, copied from the project doc when the link is made (the rules
  // check both against it). Guests never read the project doc itself (it holds ownerEmail and the Set index).
  // Optional only for parsing: every link the rules accept carries both.
  projectName: z.string().optional(),
  unitSystem: z.enum([UnitSystem.METRIC, UnitSystem.IMPERIAL] as const).optional(),
});
export type ShareLink = z.infer<typeof ShareLinkSchema>;

/** The project fields a share-link guest's viewer renders (the Breadcrumb's name, measurement units). */
export interface GuestProjectSummary {
  projectName: string;
  unitSystem: UnitSystem;
}

// ==================== /PMT annotation metadata ====================

/**
 * The round-trip metadata pdfmarkuptool writes into each exported /Annot's
 * `/PMT` entry (annotation-writer) and reads back on import (pdf-import
 * parseAnnot). App-private persistence, so zod owns it; the writer types its
 * payload as `PmtMetadata` so the emitted shape cannot drift from what the
 * reader validates.
 *
 * Tolerance semantics: the reader safeParses the WHOLE object (a payload
 * without `_pmt: '1'` is not ours and is ignored), while each optional field
 * carries `.catch(undefined)` so one malformed field degrades to "absent"
 * instead of discarding the rest of the metadata -- matching the old
 * hand-rolled guards, where a bad `points`/`rect` yielded `undefined` and
 * never threw.
 */

/** Stored points: `[[x, y], ...]` (extra tuple elements tolerated, ignored). */
export const PmtStoredPointsSchema = z.array(z.tuple([z.number(), z.number()], z.unknown()));

/** Stored exported /Rect: `[x1, y1, x2, y2]` (not necessarily min/max ordered). */
export const PmtStoredRectSchema = z.tuple(
  [z.number(), z.number(), z.number(), z.number()],
  z.unknown(),
);
export type PmtStoredRect = z.infer<typeof PmtStoredRectSchema>;

export const PmtMetadataSchema = z.object({
  _pmt: z.literal('1'),
  id: z.string().optional().catch(undefined),
  subject: z.string().optional().catch(undefined),
  text: z.string().optional().catch(undefined),
  createdByUserId: z.string().optional().catch(undefined),
  /** ISO date string (writer emits `Date#toISOString()`). */
  createdAt: z.string().optional().catch(undefined),
  groupId: z.string().optional().catch(undefined),
  /**
   * The markup type, written for the Stamps of `image` / `photo-pin` (image markup spec 4.7): it tells our
   * Stamps from foreign ones. Absent on every other subtype (the sidecar or the annot subtype gives the type).
   */
  type: z.enum(MARKUP_TYPE_VALUES).optional().catch(undefined),
  style: z
    .object({
      color: z.string().optional().catch(undefined),
      lineWidth: z.number().optional().catch(undefined),
      fontSize: z.number().optional().catch(undefined),
    })
    .optional()
    .catch(undefined),
  points: PmtStoredPointsSchema.optional().catch(undefined),
  rect: PmtStoredRectSchema.optional().catch(undefined),
});
export type PmtMetadata = z.infer<typeof PmtMetadataSchema>;

// ==================== localStorage-persisted shapes ====================

/** One BIM overlay styling rule, persisted in localStorage (useBimOverlay). */
export const BimStyleRuleSchema = z.object({
  id: z.string(),
  field: z.enum(['category', 'type', 'level']),
  operator: z.enum(['eq', 'contains']),
  value: z.string(),
  color: z.string(), // hex, e.g. '#FF5722'
  enabled: z.boolean(),
});
export type BimStyleRule = z.infer<typeof BimStyleRuleSchema>;

/** One saved scale preset, persisted in localStorage (useScalePresets). */
export const ScalePresetSchema = z.object({
  label: z.string(),
  numerator: z.number(),
  denominator: z.number(),
});
export type ScalePreset = z.infer<typeof ScalePresetSchema>;
